import { Button, type ButtonProps } from '../button/button';
import { Icon } from '../icon/icon';
import { MenuTrigger } from '../menu_trigger/menu_trigger';
import {
  Menu,
  type MenuItemWithId,
  type MenuOptionsProps,
  type MenuSelectionProps,
} from './menu';

type ContextMenuButtonProps<ItemKey extends string> = ButtonProps & {
  items: MenuItemWithId<ItemKey>[];
  headerTitle?: string;
  /**
   * Optional props to pass to the underlying menu component, e.g. an
   * aria-label for the menu.
   */
  menuProps?: Omit<MenuOptionsProps<ItemKey>, 'items' | 'headerTitle'>;
} & MenuSelectionProps<ItemKey>;

function getMenuSelectionProps<ItemKey extends string>(
  props: MenuSelectionProps<ItemKey>,
): MenuSelectionProps<ItemKey> {
  if (props.selectionMode !== 'single') {
    return {
      selectionMode: props.selectionMode,
      selection: props.selection,
      defaultSelection: props.defaultSelection,
      disallowEmptySelection: props.disallowEmptySelection,
      onSelectionChange: props.onSelectionChange,
    };
  }
  if (props.disallowEmptySelection) {
    return {
      selectionMode: 'single',
      selection: props.selection,
      defaultSelection: props.defaultSelection,
      disallowEmptySelection: true,
      onSelectionChange: props.onSelectionChange,
    };
  }
  return {
    selectionMode: 'single',
    selection: props.selection,
    defaultSelection: props.defaultSelection,
    disallowEmptySelection: props.disallowEmptySelection,
    onSelectionChange: props.onSelectionChange,
  };
}

/**
 * Renders an icon button that triggers a context menu.
 *
 * Example usage:
 * ```tsx
 * <ContextMenuButton
 *   items={[
 *     { label: 'Edit', id: 'edit', icon: 'edit' },
 *     { label: 'Delete', id: 'delete', icon: 'deletion_delete' },
 *   ]}
 * />
 * ```
 */
export function ContextMenuButton<ItemKey extends string = string>(
  props: ContextMenuButtonProps<ItemKey>,
) {
  const {
    items,
    headerTitle,
    menuProps,
    size = 's',
    selectionMode,
    selection,
    defaultSelection,
    disallowEmptySelection,
    onSelectionChange,
    ...buttonProps
  } = props;
  return (
    <MenuTrigger>
      <Button
        size={size}
        variant="tertiary"
        icon={<Icon name="menu_context" />}
        aria-label="Open context menu"
        {...buttonProps}
      />
      <Menu
        items={items}
        headerTitle={headerTitle}
        {...menuProps}
        {...getMenuSelectionProps(props)}
      />
    </MenuTrigger>
  );
}
